import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_FILTERS = {
  orderReference: '',
  orderCode: '',
  fromDate: '',
  toDate: '',
  paymentMethod: '',
  minAmount: '',
  maxAmount: '',
  payerSearch: '',
  hasDispersion: '',
};

export const DEFAULT_FORM = {
  amount: '',
  currency: 'VES',
  description: '',
  order_reference: '',
  payer_email: '',
  payer_name: '',
  payer_document: '',
  payer_phone: '',
  expires_in_minutes: 60,
  success_callback_url: '',
  payee_wallets: [],
};

const buildOrdersUrl = (projectId, filterStatus, filters) => {
  const params = new URLSearchParams();
  if (filterStatus !== 'all') params.append('status_filter', filterStatus);
  if (filters.orderReference) params.append('order_reference', filters.orderReference);
  if (filters.orderCode) params.append('order_code', filters.orderCode);
  if (filters.fromDate) params.append('from_date', new Date(filters.fromDate).toISOString());
  if (filters.toDate) params.append('to_date', new Date(filters.toDate).toISOString());
  if (filters.paymentMethod) params.append('payment_method', filters.paymentMethod);
  if (filters.minAmount) params.append('min_amount', filters.minAmount);
  if (filters.maxAmount) params.append('max_amount', filters.maxAmount);
  if (filters.payerSearch) params.append('payer_search', filters.payerSearch);
  if (filters.hasDispersion !== '') params.append('has_dispersion', filters.hasDispersion);
  const qs = params.toString();
  return `${BACKEND_URL}/api/projects/${projectId}/orders${qs ? '?' + qs : ''}`;
};

export function useOrders() {
  const [orders, setOrders] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const projectId = localStorage.getItem('activeProjectId');

  const loadOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!projectId || projectId === 'all') {
        setOrders([]);
        setLoading(false);
        return;
      }
      try {
        await axios.post(`${BACKEND_URL}/api/orders/expire-old`);
      } catch (e) {
        console.log('Could not expire old orders:', e);
      }
      const url = buildOrdersUrl(projectId, filterStatus, filters);
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, filterStatus, filters]);

  const loadWallets = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!projectId || projectId === 'all') return;
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/wallets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWallets(response.data);
    } catch (error) {
      console.error('Error loading wallets:', error);
    }
  }, [projectId]);

  useEffect(() => {
    loadOrders();
    loadWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recargar órdenes cuando cambien los filtros (con debounce).
  useEffect(() => {
    const t = setTimeout(() => {
      loadOrders();
    }, 500);
    return () => clearTimeout(t);
  }, [filterStatus, filters, loadOrders]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFilterStatus('all');
  };

  const hasActiveFilters = () => {
    return filterStatus !== 'all' || Object.values(filters).some((v) => v !== '');
  };

  const createOrder = async (orderForm) => {
    const token = localStorage.getItem('token');
    const orderData = {
      ...orderForm,
      project_id: projectId,
      amount: parseFloat(orderForm.amount),
      expires_in_minutes: parseInt(orderForm.expires_in_minutes),
      payee_wallets: orderForm.payee_wallets
        .map((pw) => ({
          wallet_id: pw.wallet_id,
          percentage: parseFloat(pw.percentage) || undefined,
          description: pw.description,
        }))
        .filter((pw) => pw.wallet_id && pw.percentage),
    };
    await axios.post(`${BACKEND_URL}/api/orders`, orderData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await loadOrders();
  };

  return {
    projectId,
    orders,
    wallets,
    loading,
    filterStatus,
    setFilterStatus,
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters,
    loadOrders,
    createOrder,
  };
}
